import { Component, computed, inject, input, OnInit } from '@angular/core';
import { FeatureToggleEnum, ProductCategoryEnum, ServiceStateEnum } from '@mwe/constants';
import { Product, TariffSelectionCardData, TextNodeType } from '@mwe/models';
import { HeadlineService } from '../../headline/headline.service';
import { NgClass } from '@angular/common';
import { HeadlineComponent } from '../../headline/headline.component';
import { TranslatePipe } from '@ngx-translate/core';
import { AddStaticImagePathPipe } from '@mwe/ui/pipes';
import { ProfileService } from '@mwe/services';
import { BadgeComponent } from '../../badge/badge/badge.component';
import { isProductCategory, isProductCategoryInternet } from '@mwe/utils';

interface TariffPriceCardTheme {
  cardClass: string;
}

interface TariffPriceCardThemes {
  loading: TariffPriceCardTheme;
  strom: TariffPriceCardTheme;
  gas: TariffPriceCardTheme;
  emobility: TariffPriceCardTheme;
  emobilityZeit: TariffPriceCardTheme;
  internet: TariffPriceCardTheme;
  'mobiles internet': TariffPriceCardTheme;
  iptv: TariffPriceCardTheme;
  voip: TariffPriceCardTheme;
  nachtstrom: TariffPriceCardTheme;
  stromrueck: TariffPriceCardTheme;
  discount: TariffPriceCardTheme;
  fernwaerme: TariffPriceCardTheme;
}

const cardThemes: TariffPriceCardThemes = {
  loading: {
    cardClass: 'bg-neutral-500',
  },
  strom: {
    cardClass: 'bg-brand-green',
  },
  gas: {
    cardClass: 'bg-secondary-light',
  },
  emobility: {
    cardClass: 'bg-secondary-light',
  },
  emobilityZeit: {
    cardClass: 'bg-primary',
  },
  internet: {
    cardClass: 'bg-primary',
  },
  'mobiles internet': {
    cardClass: 'bg-brand-green',
  },
  iptv: {
    cardClass: 'bg-primary',
  },
  voip: {
    cardClass: 'bg-primary',
  },
  nachtstrom: {
    cardClass: 'bg-secondary-light',
  },
  stromrueck: {
    cardClass: 'bg-brand-green',
  },
  discount: {
    cardClass: 'bg-warning-dirty',
  },
  fernwaerme: {
    cardClass: 'bg-primary',
  },
};

const IconMap = {
  // Strom
  SOPTB_0001: '', //OPTIMA Enstpannt
  SOPTA_0001: '', //OPTIMA Aktiv
  SOPTS_0001: '', //OPTIMA Voll Aktiv
  SMEGE_0001: '', //MEGA
  SMEGA_0001: '', //MEGA Aktiv
  SMEGS_0001: '', //MEGA Voll Aktiv
  // Gas
  GEOPTB__01: '', //OPTIMA Enstpannt
  GEOPTA__01: '', //OPTIMA Aktiv
  GEMEGE__01: '', //MEGA
  GEMEGA__01: '', //MEGA Aktiv
};

@Component({
  selector: 'mwe-tariff-card-template',
  templateUrl: './tariff-card-template.component.html',
  providers: [HeadlineService],
  imports: [NgClass, HeadlineComponent, TranslatePipe, AddStaticImagePathPipe, BadgeComponent],
})
export class TariffCardTemplateComponent implements OnInit {
  private readonly headlineService = inject(HeadlineService);
  private readonly profileService = inject(ProfileService);

  readonly category = input<string>();
  readonly categoryOverride = input<string>();
  readonly title = input<string>();
  readonly subTitle = input<string>();
  readonly hasDiscount = input<boolean>();
  readonly isDiscountKnoedlVisible = input<boolean>(false);
  readonly hasActions = input<boolean>();
  readonly hasContent = input<boolean>(true);
  readonly state = input<ServiceStateEnum>();
  readonly headlineReference = input<TextNodeType>('p');
  readonly isSpot = input(false);
  readonly spotUrl = input('');
  readonly product = input<Product | TariffSelectionCardData>();

  readonly badgeLabel = computed<string>(
    () => (this.product() as TariffSelectionCardData)?.priceIndication?.tarifkennzeichen?.beschriftung,
  );
  readonly badgeIconClasses = computed<string>(() =>
    'fas fa-'.concat((this.product() as TariffSelectionCardData)?.priceIndication?.tarifkennzeichen?.text.toLowerCase() ?? 'star'),
  );
  readonly iconName = computed<string>(() => {
    if (this.categoryOverride() === 'emobilityZeit') {
      return 'productDetails/emobilityzeit.svg';
    }

    if (!this.product()) {
      return `productDetails/${this.category().toLowerCase()}.svg`;
    }

    const tariffKey = (this.product() as Product).tarifKey
      ? (this.product() as Product).tarifKey
      : (this.product() as TariffSelectionCardData).tariffKey;

    switch (tariffKey) {
      case 'SOPTB_0001':
      case 'SMEGE_0001': {
        //OPTIMA Entspannt & MEGA
        return `productDetails/specific/Optima-STROM-Entspannt-white.svg`;
      }
      case 'SOPTA_0001':
      case 'SMEGA_0001': {
        //OPTIMA Aktiv & MEGA Aktiv
        return `productDetails/specific/Optima-STROM-Aktiv-white.svg`;
      }
      case 'SOPTS_0001':
      case 'SMEGS_0001': {
        //OPTIMA Voll Aktiv & MEGA Voll Aktiv
        return `productDetails/specific/Optima-STROM-VollAktiv-white.svg`;
      }
      case 'GEOPTB__01':
      case 'GEMEGE__01': {
        //OPTIMA Gas Entspannt & MEGA Gas
        return `productDetails/specific/Optima-GAS-Entspannt-white.svg`;
      }
      case 'GEOPTA__01':
      case 'GEMEGA__01': {
        //OPTIMA Gas Aktiv & MEGA Gas Aktiv
        return `productDetails/specific/Optima-GAS-Aktiv-white.svg`;
      }
      default: {
        const category = this.category()?.toLowerCase();

        // MWE-18222 - No Icon for new internet/ / 5G-Tariffs
        if (tariffKey?.includes('WEBTK26')) return '';

        if (isProductCategoryInternet(category) && !isProductCategory(category, ProductCategoryEnum.IPTV)) {
          return `productDetails/internet.svg`;
        }

        return `productDetails/${category}.svg`;
      }
    }
  });
  readonly theme = computed<TariffPriceCardTheme>(() => {
    if (this.state() === ServiceStateEnum.LOADING) {
      return cardThemes.loading;
    }
    if (this.hasDiscount()) {
      return cardThemes.discount;
    }
    if (this.isMvno()) {
      return cardThemes?.['mobiles internet'];
    }
    const themeKey = this.categoryOverride() ?? this.category()?.toLowerCase();
    return cardThemes[themeKey] || cardThemes.loading;
  });
  private readonly isMvno = computed<boolean>(() => {
    const category = this.category()?.toLowerCase();
    return (
      (category === 'internet' || category === 'mobiles internet') &&
      this.profileService.isFeatureToggleEnabled(FeatureToggleEnum.INTERNET_V2_ENABLED)
    );
  });

  ngOnInit(): void {
    this.headlineService.setReferenceHeadline(this.headlineReference());
  }
}
